import { isRole, roleHasCapability } from '@qim/shared'
import { pathFor } from '../routing.js'
import type { AnchorHTMLAttributes, JSX, ReactNode } from 'react'

/**
 * An account, as a way to its onboarding screen (IM-181).
 *
 * ## Why this exists
 *
 * The onboarding screen was reachable only by clicking an account's name on
 * its customer's Overview, which nobody guessed — the team's own words were
 * "that is not intuitive". Every other place an account is named (the Fleet,
 * an instance, a run, the breadcrumb, a preflight refusal) printed it as inert
 * text, so the one screen that says why an account cannot run was the hardest
 * one to find from the places a run goes wrong.
 *
 * ## Capability, never a role name
 *
 * The onboarding screen reads `GET …/onboarding`, which is `account:read`.
 * Every role holds it today, so this is nearly always a link — but the rule is
 * the one every "offered" control in the console follows, and it is not a
 * control: the route checks regardless, and a hand-typed URL for an account
 * out of scope answers 404 like any typo. A viewer without it sees the name,
 * because the name is still true.
 *
 * ## Why it navigates itself
 *
 * The same modifier check as every in-place link in the console: cmd-click and
 * middle-click open a tab because the href is real, which is the reason these
 * are anchors and not buttons.
 */

/** Whether a role is offered the way to an account's onboarding screen. */
export const mayOpenAccount = (role: string): boolean =>
  isRole(role) && roleHasCapability(role, 'account:read')

/** The account's Onboarding tab — the screen's default, so no tab segment. */
export const accountPath = (tenantId: string, accountKey: string): string =>
  pathFor({ screen: 'account', tenantId, accountKey })

export const AccountLink = ({
  tenantId,
  accountKey,
  label,
  role,
  onOpen,
  children,
  ...rest
}: {
  readonly tenantId: string
  readonly accountKey: string
  /** What to call it, when the caller already holds a label. The key otherwise. */
  readonly label?: string | undefined
  readonly role: string
  readonly onOpen: (path: string) => void
  /** Replaces the label, for a link whose words are an action. */
  readonly children?: ReactNode
} & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'onClick' | 'children'
>): JSX.Element => {
  const shown = children ?? label ?? accountKey
  if (!mayOpenAccount(role)) return <>{shown}</>
  const to = accountPath(tenantId, accountKey)
  return (
    <a
      href={to}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        onOpen(to)
      }}
      {...rest}
    >
      {shown}
    </a>
  )
}
